import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Mountain, Globe, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { CONTACT } from "../data/site";
import { useI18n } from "../i18n/LanguageContext";
import { FlagIcon } from "./FlagIcon";

const LangSwitcher = ({ scrolled }) => {
  const { lang, setLang, languages } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="lang-switcher-toggle"
        aria-label="Choisir la langue"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 text-sm font-semibold tracking-wide px-3 py-2 transition-colors ${
          scrolled ? "text-ink/70 hover:text-pine" : "text-cream/80 hover:text-cream"
        }`}
      >
        <Globe aria-hidden className="w-4 h-4" />
        <FlagIcon code={lang} />
        {lang.toUpperCase()}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="lang-switcher-menu"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-full mt-2 bg-cream border border-pine/10 shadow-xl py-2 min-w-[160px] z-50"
          >
            {languages.map((l) => (
              <button
                key={l.code}
                type="button"
                data-testid={`lang-option-${l.code}`}
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-wood-light/30 ${
                  lang === l.code ? "text-pine font-semibold" : "text-ink/70"
                }`}
              >
                <FlagIcon code={l.code} />
                <span className="flex-1 text-left">{l.name}</span>
                {lang === l.code && <Check aria-hidden className="w-4 h-4 text-wood-dark" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Navbar = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home"), key: "home" },
    { to: "/studio/glaciers", label: t("nav.glaciers"), key: "glaciers" },
    { to: "/studio/gentianes", label: t("nav.gentianes"), key: "gentianes" },
  ];

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
          scrolled ? "backdrop-blur-xl bg-cream/80 border-black/5" : "backdrop-blur-md bg-white/10 border-white/10"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
            <Mountain aria-hidden className={`w-5 h-5 transition-colors ${scrolled ? "text-pine" : "text-cream"}`} />
            <span className={`font-serif text-xl tracking-tight transition-colors ${scrolled ? "text-pine" : "text-cream"}`}>
              Vaucopin
              <span className="hidden sm:inline text-xs font-sans font-medium tracking-[0.2em] uppercase ml-3 opacity-70">
                Les 7 Laux
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.key}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors hover:text-wood ${
                    scrolled ? (isActive ? "text-pine" : "text-ink/60") : isActive ? "text-cream" : "text-cream/70"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-contact"
              className={`group flex items-center gap-2 text-sm font-semibold px-5 py-2.5 border transition-colors duration-300 ${
                scrolled
                  ? "border-pine text-pine hover:bg-pine hover:text-cream"
                  : "border-cream/60 text-cream hover:bg-cream hover:text-pine"
              }`}
            >
              {t("nav.cta")}
              <ArrowUpRight aria-hidden className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <LangSwitcher scrolled={scrolled} />
          </nav>

          <div className="md:hidden flex items-center gap-1">
            <LangSwitcher scrolled={scrolled} />
            <button
              data-testid="nav-menu-toggle"
              aria-label="Ouvrir le menu"
              onClick={() => setOpen(!open)}
              className={`p-2 ${scrolled ? "text-pine" : "text-cream"}`}
            >
              {open ? <X aria-hidden className="w-6 h-6" /> : <Menu aria-hidden className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-pine flex flex-col justify-center px-8"
          >
            {[...links, { to: "/contact", label: t("nav.cta"), key: "contact" }].map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i, duration: 0.5 }}
              >
                <Link
                  to={l.to}
                  data-testid={`mobile-nav-${l.key}`}
                  className="block font-serif text-4xl text-cream py-4 border-b border-cream/10 hover:text-wood transition-colors"
                >
                  {l.label}
                </Link>
              </motion.div>
            ))}
            <p className="mt-10 text-cream/50 text-sm">{CONTACT.phone} — {CONTACT.email}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
